/**
 * Treatment variant hero for the cfind_templates_hero_intent_value_v6 Switcheroo experiment.
 *
 * Replaces the default TemplateHeroSection with an intent-matched heading,
 * value proposition, social proof, yellow CTA, and a featured template card.
 *
 * @see https://switcheroo.atlassian.com/ui/gates/1ffdeb2c-8438-431a-9d14-e37309bc1143/key/cfind_templates_hero_intent_value_v6
 */

import { css } from "@compiled/react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import { Link } from "@/libraries/design/components/link/src/link.tsx";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";

const messages = defineMessages({
    heading: {
        id: "confluence.templates.hero.variant.heading",
        defaultMessage: "Free Project Templates for Confluence",
        description:
            "Intent-matched heading for the templates hero experiment treatment"
    },
    valueProp: {
        id: "confluence.templates.hero.variant.value-prop",
        defaultMessage:
            "Get a head start with ready-made templates for project plans, meeting notes, product specs, and 100+ more — all free with Confluence.",
        description: "Value proposition text for the templates hero treatment"
    },
    socialProof: {
        id: "confluence.templates.hero.variant.social-proof",
        defaultMessage: "Trusted by 300,000+ teams worldwide",
        description: "Social proof text for the templates hero treatment"
    },
    primaryCta: {
        id: "confluence.templates.hero.variant.cta-primary",
        defaultMessage: "Get started free",
        description: "Primary CTA button text for the templates hero treatment"
    },
    secondaryCta: {
        id: "confluence.templates.hero.variant.cta-secondary",
        defaultMessage: "Browse all templates",
        description: "Secondary CTA link text for the templates hero treatment"
    },
    trustBadge: {
        id: "confluence.templates.hero.variant.trust-badge",
        defaultMessage: "Free forever · No credit card required",
        description: "Trust badge text below primary CTA"
    },
    featuredTemplateTitle: {
        id: "confluence.templates.hero.variant.featured-title",
        defaultMessage: "Project Plan Template",
        description: "Featured template card title"
    },
    featuredTemplateDescription: {
        id: "confluence.templates.hero.variant.featured-description",
        defaultMessage:
            "Define scope, timeline, milestones, and deliverables for any project.",
        description: "Featured template card description"
    },
    learnMore: {
        id: "confluence.templates.hero.variant.learn-more",
        defaultMessage: "Learn more",
        description: "Learn more link text on featured template card"
    }
});

const TemplateHeroVariant = () => {
    const signupUrl = useLocalizedHref("/try/cloud/signup?bundle=confluence");
    const templateUrl = useLocalizedHref(
        "/software/confluence/templates/project-plan"
    );

    const intl = useIntl();
    const browseText = intl.formatMessage(messages.secondaryCta);

    const scrollToTemplates = () => {
        const tabsElement = document.getElementById(
            "confluence-template-categories"
        );
        tabsElement?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div css={containerStyles}>
            <header css={heroStyles} data-testid="template-hero-variant">
                <div css={leftColumnStyles}>
                    <h1 css={headingStyles}>
                        <FormattedMessage {...messages.heading} />
                    </h1>
                    <p css={valueStyles}>
                        <FormattedMessage {...messages.valueProp} />
                    </p>
                    <div css={socialProofStyles}>
                        <span>⭐</span>
                        <FormattedMessage {...messages.socialProof} />
                    </div>
                    <div css={ctaContainerStyles}>
                        <Button
                            css={primaryCtaStyles}
                            variant="primary_alt"
                            href={signupUrl}
                            data-testid="template-hero-variant-cta-primary"
                            dataEventProps={{
                                "data-product-key": "confluence",
                                "data-event": "clicked",
                                "data-event-component": "button",
                                "data-event-container": "heroSection",
                                "data-label": "Get started free",
                                "data-label-english": "Get started free",
                                "data-track-event":
                                    "confluence-pt.templates.hero.cta-primary"
                            }}
                        >
                            <FormattedMessage {...messages.primaryCta} />
                        </Button>
                        <Link
                            text={browseText}
                            aria-label={browseText}
                            href="#confluence-template-categories"
                            type="primary"
                            bgColor="bgBlackBlue"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToTemplates();
                            }}
                            dataEventProps={{
                                "data-product-key": "confluence",
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-event-container": "heroSection",
                                "data-label": "Browse all templates",
                                "data-label-english": "Browse all templates",
                                "data-track-event":
                                    "confluence-pt.templates.hero.cta-secondary"
                            }}
                            anchorProps={{
                                "data-testid":
                                    "template-hero-variant-cta-secondary"
                            }}
                        />
                    </div>
                    <span css={trustBadgeStyles}>
                        ✓ <FormattedMessage {...messages.trustBadge} />
                    </span>
                </div>
                <div css={rightColumnStyles}>
                    <div css={cardStyles}>
                        <div css={cardPreviewStyles}>
                            <div css={cardIconStyles}>📋</div>
                            <div css={cardLinesStyles}>
                                <div
                                    css={cardLineStyles}
                                    style={{ width: "100%" }}
                                />
                                <div
                                    css={cardLineStyles}
                                    style={{ width: "85%" }}
                                />
                                <div
                                    css={cardLineStyles}
                                    style={{ width: "70%" }}
                                />
                                <div
                                    css={cardLineStyles}
                                    style={{ width: "55%" }}
                                />
                            </div>
                        </div>
                        <div css={cardBodyStyles}>
                            <h2 css={cardTitleStyles}>
                                <FormattedMessage
                                    {...messages.featuredTemplateTitle}
                                />
                            </h2>
                            <p css={cardDescriptionStyles}>
                                <FormattedMessage
                                    {...messages.featuredTemplateDescription}
                                />
                            </p>
                            <a
                                css={cardCtaStyles}
                                href={templateUrl}
                                data-testid="template-hero-variant-card-cta"
                                data-product-key="confluence"
                                data-event="clicked"
                                data-event-component="linkButton"
                                data-event-container="heroSection"
                                data-label="Learn more"
                                data-label-english="Learn more"
                                data-track-event="confluence-pt.templates.hero.featured-learn-more"
                            >
                                <FormattedMessage {...messages.learnMore} />{" "}
                                &rarr;
                            </a>
                        </div>
                    </div>
                </div>
            </header>
        </div>
    );
};

TemplateHeroVariant.displayName = "TemplateHeroVariant";
export { TemplateHeroVariant };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyles = css({
    display: "flex",
    justifyContent: "center"
});

const heroStyles = css({
    flex: "1",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    borderRadius: "20px",
    marginBlock: "45px",
    padding: "48px 56px",
    width: "100%",
    maxWidth: "1304px",
    background: `url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg_atl_cloud_hero_small.svg) center/cover no-repeat`,
    color: wacToken("color.neutral.light0"),
    minHeight: "298px",
    marginInline: "78px",
    gap: "48px",
    [media.below.lg]: {
        marginInline: "32px",
        padding: "40px 32px",
        gap: "32px"
    },
    [media.below.sm]: {
        flexDirection: "column",
        marginInline: "24px",
        padding: "32px 24px",
        gap: "24px"
    },
    [media.below.xs]: {
        marginInline: "16px"
    }
});

const leftColumnStyles = css({
    flex: "1.2",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
});

const headingStyles = css({
    margin: "0",
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: "500",
    fontSize: "44px",
    lineHeight: "1.15",
    [media.below.sm]: {
        fontSize: "32px"
    }
});

const valueStyles = css({
    margin: "0",
    fontSize: "17px",
    fontWeight: "400",
    lineHeight: "1.5",
    color: wacToken("color.extended.blue100")
});

const socialProofStyles = css({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: wacToken("color.extended.blue100")
});

const ctaContainerStyles = css({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBlockStart: "8px",
    flexWrap: "wrap"
});

const primaryCtaStyles = css({
    [media.below.sm]: {
        width: "100%"
    }
});

const trustBadgeStyles = css({
    fontSize: "14px",
    fontWeight: "400",
    color: wacToken("color.extended.blue100")
});

const rightColumnStyles = css({
    flex: "0.8",
    display: "flex",
    justifyContent: "center",
    [media.below.sm]: {
        display: "none"
    }
});

const cardStyles = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "380px",
    backgroundColor: wacToken("color.neutral.light0"),
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: token("elevation.shadow.raised")
});

const cardPreviewStyles = css({
    height: "140px",
    backgroundColor: wacToken("color.extended.blue100"),
    display: "flex",
    alignItems: "center",
    padding: "20px 24px",
    gap: "16px"
});

const cardIconStyles = css({
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    backgroundColor: wacToken("color.primary.blue"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: wacToken("color.neutral.light0"),
    fontSize: "20px",
    flexShrink: 0
});

const cardLinesStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: 1
});

const cardLineStyles = css({
    height: "8px",
    borderRadius: "4px",
    backgroundColor: wacToken("color.neutral.light300")
});

const cardBodyStyles = css({
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
});

const cardTitleStyles = css({
    margin: "0",
    fontSize: "16px",
    fontWeight: "600",
    color: wacToken("color.primary.black")
});

const cardDescriptionStyles = css({
    margin: "0",
    fontSize: "13px",
    fontWeight: "400",
    color: wacToken("color.neutral.light700"),
    lineHeight: "1.4"
});

const cardCtaStyles = css({
    "display": "inline-flex",
    "alignItems": "center",
    "gap": "4px",
    "padding": "8px 16px",
    "fontSize": "13px",
    "fontWeight": "500",
    "color": wacToken("color.primary.blue"),
    "backgroundColor": "transparent",
    "border": `1px solid ${wacToken("color.primary.blue")}`,
    "borderRadius": "6px",
    "cursor": "pointer",
    "textDecoration": "none",
    "width": "fit-content",
    "marginTop": "4px",
    ":hover": {
        backgroundColor: wacToken("color.extended.blue100")
    }
});
